
import { useEffect, useState, useRef, useCallback } from "react";
import dayjs from "dayjs";
import {
  fetchDocumentDeadlines,
  fetchContractDeadlines,
  fetchSignedDocumentDeadlines,
} from "../services/api";

export function useDeadlines({
  token,
  dateFrom,
  dateTo,
  enabledDocs = true,
  enabledContracts = true,
  enabledSignedDocs = false,
}) {
  const [docs, setDocs] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [signedDocs, setSignedDocs] = useState([]);

  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingSignedDocs, setLoadingSignedDocs] = useState(false);

  const docsCtrlRef = useRef(null);
  const ctrsCtrlRef = useRef(null);
  const signedDocsCtrlRef = useRef(null);

  const [reloadKey, setReloadKey] = useState(0);

  const refetch = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  // DOCUMENTI
  useEffect(() => {
    if (!token || !dateFrom || !dateTo || !enabledDocs) return;

    docsCtrlRef.current?.abort();
    const ctrl = new AbortController();
    docsCtrlRef.current = ctrl;

    const params = {
      date_from: dayjs(dateFrom).format("YYYY-MM-DD"),
      date_to: dayjs(dateTo).format("YYYY-MM-DD"),
    };

    setLoadingDocs(true);

    fetchDocumentDeadlines(token, params, "payrolls", ctrl.signal)
      .then((data) => setDocs(data || []))
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        console.error(err);
      })
      .finally(() => !ctrl.signal.aborted && setLoadingDocs(false));

    return () => ctrl.abort();
  }, [token, dateFrom, dateTo, enabledDocs, reloadKey]);

  // CONTRATTI
  useEffect(() => {
    if (!token || !dateFrom || !dateTo || !enabledContracts) return;

    ctrsCtrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrsCtrlRef.current = ctrl;

    const params = {
      date_from: dayjs(dateFrom).format("YYYY-MM-DD"),
      date_to: dayjs(dateTo).format("YYYY-MM-DD"),
    };

    setLoadingContracts(true);

    fetchContractDeadlines(token, params, "payrolls", ctrl.signal)
      .then((data) => setContracts(data || []))
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        console.error(err);
      })
      .finally(() => !ctrl.signal.aborted && setLoadingContracts(false));

    return () => ctrl.abort();
  }, [token, dateFrom, dateTo, enabledContracts, reloadKey]);

  // DOCUMENTI CON FIRMA
  useEffect(() => {
    if (!token || !dateFrom || !dateTo || !enabledSignedDocs) {
      signedDocsCtrlRef.current?.abort();
      setSignedDocs([]);
      setLoadingSignedDocs(false);
      return;
    }

    signedDocsCtrlRef.current?.abort();

    const ctrl = new AbortController();
    signedDocsCtrlRef.current = ctrl;

    const params = {
      date_from: dayjs(dateFrom).format("YYYY-MM-DD"),
      date_to: dayjs(dateTo).format("YYYY-MM-DD"),
    };

    setLoadingSignedDocs(true);

    fetchSignedDocumentDeadlines(token, params, "payrolls", ctrl.signal)
      .then((data) => {
        if (!ctrl.signal.aborted) {
          setSignedDocs(
            Array.isArray(data)
              ? data
              : Array.isArray(data?.results)
                ? data.results
                : []
          );
        }
      })
      .catch((err) => {
        if (ctrl.signal.aborted || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;

        console.error("Errore caricamento documenti con firma:", err);
        setSignedDocs([]);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoadingSignedDocs(false);
      });

    return () => ctrl.abort();
  }, [token, dateFrom, dateTo, enabledSignedDocs, reloadKey]);

  return {
    docs,
    contracts,
    signedDocs,
    loading:
      (enabledDocs && loadingDocs) ||
      (enabledContracts && loadingContracts) ||
      (enabledSignedDocs && loadingSignedDocs),
    refetch,
  };
}